import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaEnvelope, FaSpinner, FaArrowLeft, FaCheckCircle, FaKey } from 'react-icons/fa';
import axios from 'axios';
import FloatingWhatsApp from '../components/FloatingWhatsApp';

const API = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tempPassword, setTempPassword] = useState(null); // For testing when email not configured

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setTempPassword(null);
    
    try {
      const response = await axios.post(`${API}/api/auth/forgot-password`, { email });
      setSuccess(true);
      
      // If email service not configured, show temp password (for testing)
      if (response.data.temp_password) {
        setTempPassword(response.data.temp_password);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to process request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" 
         style={{ background: 'linear-gradient(135deg, #0A1929 0%, #1E3A5F 100%)' }}>
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold mb-2" style={{ color: '#FFD700' }}>
            Arth Gurukul
          </h1>
          <p className="text-[#FFD700] text-lg font-semibold">Your School for Lifelong Financial Wellness</p>
        </div>

        {/* Forgot Password Card */}
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl shadow-2xl p-8 border border-[#2D5A8F]">
          {!success ? (
            <>
              <div className="text-center mb-6">
                <div className="bg-cyan-500/20 p-4 rounded-full inline-block mb-4">
                  <FaKey className="text-cyan-400 text-3xl" />
                </div>
                <h2 className="text-2xl font-bold text-white">Forgot Password?</h2>
                <p className="text-gray-400 text-sm mt-2">
                  Enter your email address and we'll send you a new password.
                </p>
              </div>
              
              {error && (
                <div className="mb-4 p-3 bg-red-500/20 border border-red-500 rounded-lg text-red-200 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaEnvelope className="text-gray-400" />
                    </div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20 transition-all"
                      placeholder="Enter your email"
                      required
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 px-4 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] focus:outline-none transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? (
                    <>
                      <FaSpinner className="animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center" data-testid="forgot-success">
              <div className="bg-green-500/20 p-4 rounded-full inline-block mb-4">
                <FaCheckCircle className="text-green-400 text-3xl" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Check Your Email</h2>
              <p className="text-gray-300 mb-4">
                If an account exists with that email, we've sent a new password to it.
              </p>
              
              {/* Show temp password for testing (when email not configured) */}
              {tempPassword && (
                <div className="mb-4 p-4 bg-yellow-500/20 border border-yellow-500 rounded-lg">
                  <p className="text-yellow-400 text-sm mb-2">
                    <strong>Testing Mode:</strong> Email service not configured.
                  </p>
                  <p className="text-white">Your new password:</p>
                  <p className="text-2xl font-mono text-yellow-400 mt-1" data-testid="temp-password">
                    {tempPassword}
                  </p>
                </div>
              )}
              
              <p className="text-gray-400 text-sm">
                Please change your password immediately after logging in.
              </p>
            </div>
          )}

          {/* Back to Login */}
          <div className="mt-6 text-center">
            <Link 
              to="/login" 
              className="text-[#FFD700] hover:text-[#D4AF37] font-medium transition-colors inline-flex items-center"
            >
              <FaArrowLeft className="mr-2" />
              Back to Login
            </Link>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center space-y-2">
          <p className="text-gray-400 text-xs">© 2026 Arth Gurukul | Tech, Tools &amp; Training Powered by MDRT School | By Financial Nirvana. All rights reserved.</p>
          <p className="text-[#FFD700] text-xs">Your School for Lifelong Financial Wellness</p>
        </div>
      </div>
      
      {/* Floating WhatsApp Button */}
      <FloatingWhatsApp 
        phoneLink="https://wa.link/c15uzz"
        message="Need help? Chat with us!"
      />
    </div>
  );
};

export default ForgotPassword;
